import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, EnvelopeSimple, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import Logo from "../components/Logo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      // Backend always returns 200 — only network errors land here
      setErr(e?.response?.data?.detail || "Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-app grain flex flex-col" data-testid="forgot-password-page">
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <Link
            to="/login"
            data-testid="forgot-back"
            className="mb-8 flex items-center gap-2 text-muted-fg hover:text-primary-fg text-xs font-mono uppercase tracking-widest"
          >
            <ArrowLeft size={14} /> Back to sign in
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <Logo size={32} />
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-muted-fg">
              Breakout Scanner
            </div>
          </div>

          {!sent ? (
            <>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-pos mb-2">
                /// Reset password
              </div>
              <h1 className="text-3xl font-medium tracking-tight text-primary-fg mb-2">
                Forgot your password?
              </h1>
              <p className="text-sm text-secondary-fg mb-8">
                No worries. Enter your email and we'll send you a one-time reset link valid for{" "}
                <span className="text-primary-fg">1 hour</span>.
              </p>

              <form onSubmit={submit} className="space-y-3">
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
                    Email
                  </span>
                  <div className="relative mt-1">
                    <EnvelopeSimple
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg"
                    />
                    <input
                      data-testid="forgot-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="w-full pl-9 pr-3 py-2.5 bg-surface border border-subtle rounded-sm text-primary-fg placeholder:text-muted-fg focus:border-focus transition font-mono text-sm"
                    />
                  </div>
                </label>

                {err && (
                  <div
                    data-testid="forgot-error"
                    className="text-neg text-sm bg-neg-soft border border-neg-soft px-3 py-2 rounded-sm"
                  >
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="forgot-submit"
                  className="w-full mt-2 px-4 py-3 rounded-sm font-medium hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
                >
                  {busy ? "Sending…" : "Send reset link"}
                  {!busy && <ArrowRight size={16} weight="bold" />}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-sent" className="border border-pos-soft bg-pos-soft/40 rounded-sm p-6">
              <CheckCircle size={28} weight="fill" className="text-pos mb-3" />
              <h2 className="text-2xl font-medium text-primary-fg mb-2">Check your inbox</h2>
              <p className="text-sm text-secondary-fg leading-relaxed">
                If an account exists with <span className="font-mono text-primary-fg">{email}</span>,
                a reset link has been sent. The link expires in{" "}
                <span className="text-primary-fg">1 hour</span>.
              </p>
              <p className="text-xs text-muted-fg mt-4">
                Don't see the email? Check your spam folder, or wait a minute and try again.
              </p>
              <Link
                to="/login"
                className="mt-5 inline-flex items-center gap-2 text-pos text-sm font-medium hover:underline"
              >
                Back to sign in →
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
